import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "@/data/blog";
import { useSeo } from "@/hooks/useSeo";

export default function Blog() {
  useSeo({
    title: "ZAKE Termal Blog — Termal Görüntüleme & Önleyici Güvenlik Rehberi",
    description:
      "Endüstriyel termal görüntüleme, kestirimci bakım, GES denetimi, sera tarımı ve önleyici güvenlik üzerine güncel makaleler ve rehberler.",
    canonical: "https://termal.datarapor.com/blog",
    keywords: "termal görüntüleme blog, kestirimci bakım, ges termal denetim, sera termal, yangın güvenliği rehber",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      url: "https://termal.datarapor.com/blog",
      name: "ZAKE Termal Blog",
      publisher: { "@id": "https://termal.datarapor.com/#organization" },
      blogPost: Object.values(BLOG_POSTS).map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://termal.datarapor.com/blog/${p.slug}`,
        datePublished: p.date,
        description: p.description,
      })),
    },
  });

  const posts = Object.values(BLOG_POSTS).sort((a, b) =>
    b.date.localeCompare(a.date)
  );

  return (
    <div data-testid="page-blog" className="relative">
      <div className="absolute inset-x-0 top-0 h-[400px] bg-grid opacity-30 pointer-events-none" />
      <div
        className="absolute inset-x-0 top-0 h-[500px] pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at 30% 0%, rgba(255,90,0,0.18), transparent 60%)",
        }}
      />

      <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4">
          / Blog & Rehberler
        </div>
        <h1 className="text-[2.25rem] sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
          Termal güvenlik <span className="thermal-text">bilgi tabanı</span>
        </h1>
        <p className="mt-6 max-w-2xl text-slate-300 text-base sm:text-lg leading-relaxed">
          Sektörel rehberler, vaka analizleri ve teknik makaleler — saha
          uygulamalarından öğrenilenler.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {posts.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`blog-card-${p.slug}`}
            >
              <Link
                to={`/blog/${p.slug}`}
                className="group block terminal-border overflow-hidden hover:border-thermal-orange/60 transition-colors"
              >
                <div
                  className="aspect-[16/10] bg-cover bg-center relative"
                  style={{ backgroundImage: `url(${p.cover})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06090F] via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] text-thermal-orange bg-[#06090F]/80 px-2 py-1 backdrop-blur">
                    / {p.category}
                  </div>
                </div>
                <div className="p-5">
                  <h2 className="text-lg font-semibold leading-tight group-hover:text-thermal-orange transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-slate-400 line-clamp-3 leading-relaxed">
                    {p.excerpt}
                  </p>
                  <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" /> {p.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> {p.readTime}
                    </span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-thermal-orange" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
